import * as React from 'react';
import { useTranslation } from 'react-i18next';
import SwipableViews from 'react-swipeable-views';
import Paper from '@mui/material/Paper';

import { useTheme } from '../../../theme';
import { Locale } from '../../../types';
import { useMemo } from 'react';
import FareCode from '../FareCode/FareCode';
import { Tabs } from '@shared/ui/Tabs';
import { textIsLink } from '../../utils';
import { FareRulesQuery } from '@websky/graphql';
import { SafeHtml } from '@shared/ui';

export interface Props {
	showTranslateTab?: boolean;
	onLangChange: (lang: Locale) => void;
	fareRulesBySegmentsPassengersMap: { [segmentId: string]: FareRulesQuery['FareRules']['rulesForSegments'] };
	tabsSegments: { id: string; title: string; isDisabled: boolean }[];
	setSegment: React.Dispatch<React.SetStateAction<string>>;
	segment: string;
	selectLang: React.Dispatch<React.SetStateAction<Locale>>;
	selectedLang: Locale;
	fareRules: FareRulesQuery;
}

const FareConditions: React.FunctionComponent<Props> = ({
	onLangChange,
	showTranslateTab,
	fareRulesBySegmentsPassengersMap,
	tabsSegments,
	selectLang,
	setSegment,
	segment,
	selectedLang,
	fareRules
}) => {
	const { FareConditions: css } = useTheme('FareConditions');
	const { t } = useTranslation('FareConditions');

	const handleLangChange: React.Dispatch<Locale> = lang => {
		if (lang === selectedLang) {
			return;
		}
		onLangChange(lang);
		selectLang(lang);
	};

	const fareRulesSuccessfullyParsed = useMemo(() => Object.keys(fareRulesBySegmentsPassengersMap).length > 0, [
		fareRulesBySegmentsPassengersMap
	]);

	const ruleTextRender = (text: string, index: number) => {
		return (
			<div key={index}>
				{textIsLink(text) ? (
					<a href={text} target="_blank">
						{t('See more details on this page')}
					</a>
				) : (
					<SafeHtml tag="pre" html={text} />
				)}
			</div>
		);
	};

	const languages = [
		{ locale: Locale.English, label: t('Original') },
		{ locale: Locale.Russian, label: t('Translate to russian*') }
	];

	return (
		<Paper className={css.paperRoot}>
			<div className={css.title}>{t('Fare conditions')}</div>

			{fareRulesSuccessfullyParsed && (
				<div className={css.tabs__wrapper}>
					<Tabs
						classes={{ tabs: css.tabs__segments, tab: css.tab }}
						tabs={tabsSegments.map(tab => ({ label: tab.title }))}
						active={tabsSegments.findIndex(tab => tab.id === segment)}
						onChange={index => {
							setSegment(tabsSegments[index].id);
						}}
					/>

					{showTranslateTab && (
						<Tabs
							classes={{ tabs: css.tabs__languages, tab: css.tab }}
							tabs={languages.map(language => ({ label: language.label }))}
							active={languages.findIndex(lang => lang.locale === selectedLang)}
							onChange={index => {
								handleLangChange(languages[index].locale);
							}}
						/>
					)}
				</div>
			)}

			{
				<>
					<SwipableViews
						index={Object.keys(fareRulesBySegmentsPassengersMap).indexOf(segment)}
						onChangeIndex={index => {
							setSegment(Object.keys(fareRulesBySegmentsPassengersMap)[index]);
						}}
					>
						{Object.keys(fareRulesBySegmentsPassengersMap).map(key => (
							<div key={key} className={css.fareConditionsContent}>
								<div>
									<pre>
										{segment && (
											<FareCode passengersRules={fareRulesBySegmentsPassengersMap[segment]} />
										)}
									</pre>
									{segment &&
										fareRulesBySegmentsPassengersMap[segment]?.[0]?.rules?.map((rule, index) =>
											ruleTextRender(rule.text, index)
										)}
								</div>
							</div>
						))}
					</SwipableViews>

					{!fareRulesSuccessfullyParsed && (
						<div className={css.fareConditionsContent}>
							<div>
								{fareRules.FareRules.rulesForSegments?.map(rule =>
									rule.rules.map((rule, index) => ruleTextRender(rule.text, index))
								)}
							</div>
						</div>
					)}
				</>
			}
		</Paper>
	);
};

export default FareConditions;
